import { useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/* ────────────────────────────────────────────────────────────────────────────
   Tabs and segmented controls.

   Both use a roving tabindex: one stop in the tab order, arrow keys move
   between options, Home/End jump to the ends.
   ──────────────────────────────────────────────────────────────────────────── */

/** Shared arrow-key handler for a single-row list of options. */
function useRovingKeys<T extends string>(
  values: readonly T[],
  value: T,
  onChange: (v: T) => void,
) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const current = Math.max(0, values.indexOf(value));
    let next: number;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = (current - 1 + values.length) % values.length;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = (current + 1) % values.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else {
      next = values.length - 1;
    }
    event.preventDefault();
    const target = values[next];
    if (target === undefined) return;
    onChange(target);
    refs.current[next]?.focus();
  }

  return { refs, onKeyDown };
}

export interface TabItem {
  id: string;
  label: ReactNode;
  count?: number;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  variant?: 'underline' | 'pill';
  className?: string;
}

export function Tabs({ items, value, onChange, variant = 'underline', className }: TabsProps) {
  const ids = items.map((i) => i.id);
  const { refs, onKeyDown } = useRovingKeys(ids, value, onChange);
  const underline = variant === 'underline';

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      onKeyDown={onKeyDown}
      className={cn(
        'no-scrollbar flex min-w-0 items-center overflow-x-auto',
        underline ? 'gap-1 border-b border-line' : 'gap-1 rounded-lg border border-line bg-elevated p-1',
        className,
      )}
    >
      {items.map((item, index) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            ref={(node) => {
              refs.current[index] = node;
            }}
            role="tab"
            type="button"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.id)}
            className={cn(
              'relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-medium transition-colors duration-150 ease-swift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70',
              underline
                ? cn(
                    'h-9 rounded-t px-3 text-xs',
                    active ? 'text-fg' : 'text-muted hover:text-fg',
                  )
                : cn(
                    'h-8 rounded-md px-3 text-xs',
                    active
                      ? 'bg-surface text-fg shadow-card'
                      : 'text-muted hover:bg-surface/60 hover:text-fg',
                  ),
            )}
          >
            {item.label}
            {item.count !== undefined ? (
              <span
                className={cn(
                  'rounded px-1 font-mono text-3xs tabular-nums',
                  active ? 'bg-bull/15 text-bull' : 'bg-elevated text-subtle',
                  !underline && active && 'bg-bull/15',
                )}
              >
                {item.count}
              </span>
            ) : null}
            {underline && active ? (
              <span
                aria-hidden="true"
                className="absolute inset-x-1.5 bottom-0 h-0.5 rounded-full bg-bull-sheen"
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface SegmentedItem<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
}

export interface SegmentedProps<T extends string> {
  items: SegmentedItem<T>[];
  value: T;
  onChange: (v: T) => void;
  size?: 'sm' | 'md';
  className?: string;
}

/** Compact exclusive choice — intervals, sort orders, buy/sell side. */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  size = 'md',
  className,
}: SegmentedProps<T>) {
  const values = items.map((i) => i.value);
  const { refs, onKeyDown } = useRovingKeys(values, value, onChange);

  return (
    <div
      role="radiogroup"
      onKeyDown={onKeyDown}
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-md border border-line bg-elevated p-0.5',
        className,
      )}
    >
      {items.map((item, index) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            role="radio"
            type="button"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.value)}
            className={cn(
              'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded font-medium transition-all duration-150 ease-swift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70',
              size === 'sm' ? 'h-6 px-2 text-3xs' : 'h-7 px-2.5 text-2xs',
              active
                ? 'bg-surface text-fg shadow-card'
                : 'text-muted hover:bg-surface/60 hover:text-fg',
            )}
          >
            {item.icon ? <span className="inline-flex shrink-0">{item.icon}</span> : null}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
